/**
 * Daily Dashboard - Day-focused personal dashboard
 */

import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Screen } from '../design/primitives';
import { SPACING } from '../design/tokens';
import { apiClient } from '../api/apiConfig';
import {
  DayScoreHeader,
  HealthTile,
  InboxTile,
  TopPriorityTile,
  CalendarTile,
  FinanceTile,
} from '../components/dashboard';

interface HealthData {
  readiness_score: number | null;
  hrv_rmssd: number | null;
  baseline_7d_hrv: number | null;
  resting_hr: number | null;
  baseline_7d_resting_hr: number | null;
  sleep_debt_minutes: number | null;
  sleep_duration_minutes: number | null;
  sleep_efficiency_pct: number | null;
  sleep_score: number | null;
  rem_sleep_minutes: number | null;
  deep_sleep_minutes: number | null;
  light_sleep_minutes: number | null;
  steps: number | null;
  workout_minutes: number | null;
  training_minutes_week_excl_walking: number | null;
}

interface WellnessAvg {
  avg: number | null;
  latest: number | null;
}

interface WellnessData {
  mood: WellnessAvg;
  energy: WellnessAvg;
  clarity: WellnessAvg;
  irritability: WellnessAvg;
}

interface WeightData {
  current: number | null;
  target: number | null;
  unit: string;
}

interface Task {
  id: number;
  description: string;
  top_priority?: boolean;
  due_date?: string;
  is_done: boolean;
  project?: { name: string };
}

interface CalendarEvent {
  id: number;
  title: string;
  start: string;
  end: string;
  event_type?: string;
  is_meeting?: boolean;
}

const Dashboard: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [wellnessData, setWellnessData] = useState<WellnessData>({
    mood: { avg: null, latest: null },
    energy: { avg: null, latest: null },
    clarity: { avg: null, latest: null },
    irritability: { avg: null, latest: null },
  });
  const [weightData, setWeightData] = useState<WeightData>({ current: null, target: null, unit: 'kg' });
  const [inboxEmails, setInboxEmails] = useState<any[]>([]);
  const [topPriorityTasks, setTopPriorityTasks] = useState<Task[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(true);

  // Calculate day score: Recovery (40%) + Sleep (30%) + Wellness (30%)
  const calculateDayScore = (): number | null => {
    const scores: { value: number | null; weight: number }[] = [
      { value: healthData?.readiness_score ?? null, weight: 0.4 },
      { value: healthData?.sleep_score ?? null, weight: 0.3 },
      { value: getWellnessScore(), weight: 0.3 },
    ];

    const available = scores.filter(s => s.value !== null);
    if (available.length === 0) return null;

    const totalWeight = available.reduce((sum, s) => sum + s.weight, 0);
    const weightedSum = available.reduce((sum, s) => sum + (s.value! * s.weight), 0);
    return Math.round(weightedSum / totalWeight);
  };

  // Convert wellness (1-3) to 0-100 scale using averages
  const getWellnessScore = (): number | null => {
    const values = [wellnessData.mood.avg, wellnessData.energy.avg, wellnessData.clarity.avg, wellnessData.irritability.avg].filter(v => v !== null) as number[];
    if (values.length === 0) return null;
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    return Math.round(((avg - 1) / 2) * 100);
  };

  // Load main dashboard data
  useEffect(() => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    setLoading(true);

    Promise.all([
      apiClient.get(`/api/daily-health-aggregates/?date=${dateStr}`).catch(() => ({ data: [] })),
      apiClient.get('/api/moods/').catch(() => ({ data: [] })),
      apiClient.get('/api/energy/').catch(() => ({ data: [] })),
      apiClient.get('/api/mental-clarity/').catch(() => ({ data: [] })),
      apiClient.get('/api/irritability/').catch(() => ({ data: [] })),
      apiClient.get('/api/tasks/', { params: { is_done: false } }).catch(() => ({ data: [] })),
      apiClient.get('/api/emails/', { params: { is_handled: false, is_archived: false, is_newsletter: false, page_size: 20 } }).catch(() => ({ data: { results: [] } })),
      apiClient.get('/api/health-metrics/', { params: { metric_type: 'weight', ordering: '-date' } }).catch(() => ({ data: [] })),
      apiClient.get('/api/health-goals/', { params: { metric_type: 'weight', status: 'active' } }).catch(() => ({ data: [] })),
    ]).then(([healthRes, moodRes, energyRes, clarityRes, irritabilityRes, tasksRes, emailsRes, weightRes, weightGoalRes]) => {
      setHealthData(healthRes.data?.[0] || null);

      // Compute avg of last 10 + latest for each wellness metric
      const computeWellnessAvg = (entries: any[]): WellnessAvg => {
        // Sort by created_at descending
        const sorted = [...entries].sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        const last10 = sorted.slice(0, 10);
        if (last10.length === 0) return { avg: null, latest: null };
        const avg = last10.reduce((sum: number, e: any) => sum + e.level, 0) / last10.length;
        return { avg: Math.round(avg * 100) / 100, latest: last10[0].level };
      };

      setWellnessData({
        mood: computeWellnessAvg(moodRes.data),
        energy: computeWellnessAvg(energyRes.data),
        clarity: computeWellnessAvg(clarityRes.data),
        irritability: computeWellnessAvg(irritabilityRes.data),
      });

      // Weight: latest entry + active goal target
      const weightEntries = Array.isArray(weightRes.data) ? weightRes.data : [];
      const weightGoals = Array.isArray(weightGoalRes.data) ? weightGoalRes.data : [];
      setWeightData({
        current: weightEntries.length > 0 ? weightEntries[0].value : null,
        target: weightGoals.length > 0 ? parseFloat(weightGoals[0].target_value) : null,
        unit: weightEntries.length > 0 ? weightEntries[0].unit || 'kg' : 'kg',
      });

      // Top priority tasks
      const allTasks = tasksRes.data as Task[];
      setTopPriorityTasks(allTasks.filter(t => t.top_priority && !t.is_done));

      // Unhandled emails (paginated response)
      const allEmails = emailsRes.data?.results || (Array.isArray(emailsRes.data) ? emailsRes.data : []);
      allEmails.sort((a: any, b: any) =>
        new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
      );
      setInboxEmails(allEmails);

      setLoading(false);
    });
  }, [selectedDate]);

  // Load upcoming calendar events (next 7 days)
  useEffect(() => {
    setCalendarLoading(true);
    const now = new Date();
    const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    apiClient.get('/api/calendar_events/')
      .then((res) => {
        const allEvents = res.data || [];
        const upcoming = allEvents.filter((e: CalendarEvent) => {
          const eventStart = new Date(e.start);
          return eventStart >= now && eventStart <= in7Days;
        });
        upcoming.sort((a: CalendarEvent, b: CalendarEvent) =>
          new Date(a.start).getTime() - new Date(b.start).getTime()
        );
        setCalendarEvents(upcoming);
      })
      .catch(() => setCalendarEvents([]))
      .finally(() => setCalendarLoading(false));
  }, []);

  const handleMarkDone = (taskId: number) => {
    apiClient.patch(`/api/tasks/${taskId}/`, { is_done: true }).then(() => {
      setTopPriorityTasks(prev => prev.filter(t => t.id !== taskId));
    }).catch(err => console.error('Error marking task done:', err));
  };

  return (
    <Screen>
      <DayScoreHeader
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        dayScore={calculateDayScore()}
        loading={loading}
      />

      {/* Top Priority - Full width, prominent */}
      <div style={{ marginBottom: SPACING[16] }}>
        <TopPriorityTile
          tasks={topPriorityTasks}
          loading={loading}
          onMarkDone={handleMarkDone}
        />
      </div>

      {/* 3-column responsive grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: SPACING[16],
        }}
      >
        <HealthTile
          readinessScore={healthData?.readiness_score ?? null}
          hrvRmssd={healthData?.hrv_rmssd ?? null}
          hrvBaseline={healthData?.baseline_7d_hrv ?? null}
          restingHr={healthData?.resting_hr ?? null}
          rhrBaseline={healthData?.baseline_7d_resting_hr ?? null}
          sleepDebtMinutes={healthData?.sleep_debt_minutes ?? null}
          sleepDurationMinutes={healthData?.sleep_duration_minutes ?? null}
          sleepEfficiency={healthData?.sleep_efficiency_pct ?? null}
          sleepScore={healthData?.sleep_score ?? null}
          remMinutes={healthData?.rem_sleep_minutes ?? null}
          deepMinutes={healthData?.deep_sleep_minutes ?? null}
          lightMinutes={healthData?.light_sleep_minutes ?? null}
          wellness={wellnessData}
          weightCurrent={weightData.current}
          weightTarget={weightData.target}
          weightUnit={weightData.unit}
          steps={healthData?.steps ?? null}
          workoutMinutes={healthData?.workout_minutes ?? null}
          trainingMinutesWeek={healthData?.training_minutes_week_excl_walking ?? null}
          loading={loading}
        />

        <InboxTile
          emails={inboxEmails}
          loading={loading}
          onEmailHandled={() => {
            // Re-fetch inbox emails after handling
            apiClient.get('/api/emails/', { params: { is_handled: false, is_archived: false, is_newsletter: false, page_size: 20 } })
              .then(res => {
                const emails = res.data?.results || (Array.isArray(res.data) ? res.data : []);
                emails.sort((a: any, b: any) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime());
                setInboxEmails(emails);
              }).catch(() => {});
          }}
        />

        <CalendarTile
          events={calendarEvents}
          loading={calendarLoading}
        />

        <FinanceTile />
      </div>
    </Screen>
  );
};

export default Dashboard;
